import type { ReactNode } from 'react';
import clsx from 'clsx';
import { AlertTriangle, CheckCircle2, Info, XCircle, Inbox } from 'lucide-react';
import { ApiError } from '@/lib/api';

export function Spinner({ className }: { className?: string }) {
  return (
    <svg className={clsx('animate-spin text-current', className ?? 'h-5 w-5')} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

export function LoadingBlock({ text = 'Wird geladen …', className }: { text?: string; className?: string }) {
  return (
    <div className={clsx('flex items-center justify-center gap-3 py-12 text-sm text-slate-500', className)}>
      <Spinner />
      {text}
    </div>
  );
}

export function EmptyState({ title, description, icon, action }: { title: string; description?: string; icon?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
      <div className="mb-3 rounded-full bg-slate-100 p-3 text-slate-400">{icon ?? <Inbox className="h-6 w-6" />}</div>
      <p className="font-medium text-slate-700">{title}</p>
      {description && <p className="mt-1 max-w-md text-sm text-slate-500">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

type AlertTone = 'info' | 'success' | 'warning' | 'error' | 'legal';

const alertStyles: Record<AlertTone, string> = {
  info: 'border-blue-200 bg-blue-50 text-blue-900',
  success: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  warning: 'border-amber-200 bg-amber-50 text-amber-900',
  error: 'border-red-200 bg-red-50 text-red-900',
  legal: 'border-purple-200 bg-purple-50 text-purple-900',
};

const alertIcons: Record<AlertTone, ReactNode> = {
  info: <Info className="h-5 w-5 shrink-0" />,
  success: <CheckCircle2 className="h-5 w-5 shrink-0" />,
  warning: <AlertTriangle className="h-5 w-5 shrink-0" />,
  error: <XCircle className="h-5 w-5 shrink-0" />,
  legal: <AlertTriangle className="h-5 w-5 shrink-0" />,
};

export function Alert({ tone = 'info', title, children, className, action }: { tone?: AlertTone; title?: string; children?: ReactNode; className?: string; action?: ReactNode }) {
  return (
    <div className={clsx('flex gap-3 rounded-lg border p-3 text-sm', alertStyles[tone], className)} role={tone === 'error' ? 'alert' : 'status'}>
      {alertIcons[tone]}
      <div className="min-w-0 flex-1">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={clsx(title && 'mt-0.5')}>{children}</div>}
      </div>
      {action}
    </div>
  );
}

/** Hinweis für rechtlich relevante Aktionen (Kündigung, Mieterhöhung, Mahngebühr, NK-Abrechnung …) */
export function LegalNotice({ children }: { children?: ReactNode }) {
  return (
    <Alert tone="legal" title="Rechtliche Prüfung erforderlich">
      {children ?? 'Das System trifft keine rechtsverbindlichen Entscheidungen. Bitte Fristen, Formvorschriften und Vertragsgrundlagen prüfen.'}
    </Alert>
  );
}

export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  const message = error instanceof ApiError ? error.message : 'Die Daten konnten nicht geladen werden.';
  return (
    <Alert
      tone="error"
      title="Fehler"
      action={
        retry && (
          <button type="button" onClick={retry} className="text-sm font-medium underline">
            Erneut versuchen
          </button>
        )
      }
    >
      {message}
    </Alert>
  );
}
